import { UserNotificationsNotifications } from "@microtica/ms-notification-sdk";
import { Project } from "@microtica/ms-project-sdk";
import { Drawer, Empty } from "antd";
import { useState } from "react";
import { useRecoilState } from "recoil";
import { getNotificationService, getProjectService } from "../../backend";
import { currentProjectState } from "../../recoil/project";
import { setProjectLocalStorage } from "../../utils/local-storage";
import Notification from "./Notification";
import { BellRing, AlertTriangle, Calendar, FolderOpen, CheckCheck, X } from "lucide-react";

interface NotificationsDrawerProps {
    projects: Project[],
    notifications: UserNotificationsNotifications[],
    type: "Incidents" | "Updates";
    visible?: boolean;
    onClose: () => void;
    onMarkAsRead?: () => void;
}

const NotificationsDrawer = ({
    projects,
    notifications,
    type,
    visible,
    onClose,
    onMarkAsRead
}: NotificationsDrawerProps) => {
    const [currentProject, setCurrentProject] = useRecoilState(currentProjectState);
    const [sortByProject, setSortByProject] = useState<boolean>(false);

    const updateProjectInfo = async (projectId: string) => {
        const { data: project } = await getProjectService().getProject(projectId);
        setProjectLocalStorage(project);
        setCurrentProject(project!);
    }

    const handleMarkAllNotificationsAsRead = async () => {
        await getNotificationService().markNotificationsAsRead(currentProject.id);
        onMarkAsRead?.();
        onClose();
    }

    const unreadCount = notifications.filter(n => !n.isRead).length;

    return (
        <Drawer
            placement="right"
            open={visible}
            destroyOnClose={true}
            closable={false}
            onClose={onClose}
            width={420}
            className="notifications-drawer"
            styles={{
                header: { display: 'none' },
                body: { padding: 0 }
            }}
        >
            {/* Custom Header */}
            <div className="notifications-drawer-header">
                <div className="notifications-drawer-header-content">
                    <div className="notifications-drawer-title-row">
                        <div className="notifications-drawer-icon">
                            {type === "Incidents" ? 
                                <AlertTriangle size={20} /> : 
                                <BellRing size={20} />
                            }
                        </div>
                        <div className="notifications-drawer-title-content">
                            <h2 className="notifications-drawer-title">{type}</h2>
                            {unreadCount > 0 && (
                                <span className="notifications-drawer-badge">{unreadCount} new</span>
                            )}
                        </div>
                    </div>
                    <button 
                        className="notifications-drawer-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>
            </div>

            {/* Filter & Actions Bar */}
            <div className="notifications-drawer-actions">
                <div className="notifications-drawer-tabs">
                    <button 
                        className={`notifications-drawer-tab ${!sortByProject ? 'active' : ''}`}
                        onClick={() => setSortByProject(false)}
                    >
                        <Calendar size={14} />
                        <span>By date</span>
                    </button>
                    <button 
                        className={`notifications-drawer-tab ${sortByProject ? 'active' : ''}`}
                        onClick={() => setSortByProject(true)}
                    >
                        <FolderOpen size={14} />
                        <span>By project</span>
                    </button>
                </div>
                {notifications.length > 0 && (
                    <button 
                        className="notifications-drawer-mark-read"
                        onClick={handleMarkAllNotificationsAsRead}
                    >
                        <CheckCheck size={14} />
                        <span>Mark all read</span>
                    </button>
                )}
            </div>

            {/* Notifications List */}
            <div className="notifications-drawer-list">
                {notifications.length === 0 ? (
                    <div className="notifications-drawer-empty">
                        <div className="notifications-drawer-empty-icon">
                            {type === "Incidents" ? 
                                <AlertTriangle size={32} /> : 
                                <BellRing size={32} />
                            }
                        </div>
                        <h3>No {type.toLowerCase()}</h3>
                        <p>
                            {type === "Incidents" 
                                ? "You're all caught up! No active incidents." 
                                : "You're all caught up! No new updates."}
                        </p>
                    </div>
                ) : (
                    notifications
                        .sort((a, b) => sortByProject 
                            ? a.projectId.localeCompare(b.projectId) 
                            : b.timestamp.localeCompare(a.timestamp)
                        )
                        .map(notification => (
                            <Notification
                                {...notification}
                                key={notification.id}
                                type={type}
                                projectName={projects.find(p => p.id === notification.projectId)?.name!}
                                onClose={onClose}
                                updateProjectInfo={updateProjectInfo}
                            />
                        ))
                )}
            </div>
        </Drawer>
    )
}

export default NotificationsDrawer;
